import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { Loader, AlertCircle } from "lucide-react";
import type { Survey } from "../types/survey";
import SEO from "./SEO";

export default function SurveyList() {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchSurveys() {
      try {
        setLoading(true);
        setError(null);

        console.log("Fetching surveys...");
        const { data, error } = await supabase
          .from("surveys")
          .select("*")
          .eq("is_active", true);

        if (error) {
          console.error("Error fetching surveys:", error);
          setError("Failed to load surveys. Please try again later.");
        } else {
          console.log("Surveys data:", data);
          setSurveys(data || []);
        }
      } catch (err) {
        console.error("Unexpected error:", err);
        setError("An unexpected error occurred. Please try again later.");
      } finally {
        setLoading(false);
      }
    }

    fetchSurveys();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader className="h-12 w-12 text-[#F98B3D] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
        <div className="flex">
          <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
            <p className="text-sm text-red-700 mt-2">
              This could be due to a connection issue with the database. Please
              check your Supabase configuration.
            </p>
            <div className="mt-4 p-4 bg-white rounded border border-red-200">
              <h3 className="text-sm font-medium text-red-800">
                Troubleshooting Steps:
              </h3>
              <ol className="mt-2 text-sm text-red-700 list-decimal list-inside">
                <li>
                  Verify that your Supabase URL and anon key are correct in the
                  .env file
                </li>
                <li>Check if your Supabase project is online and accessible</li>
                <li>
                  Ensure that the database tables have been created correctly
                </li>
                <li>
                  Verify that Row Level Security (RLS) policies are properly
                  configured
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (surveys.length === 0) {
    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
        <div className="flex">
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              No active surveys found. Please check back later.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <SEO
        title="Available Surveys | One80Labs"
        description="View and participate in available surveys from One80Labs. Share your feedback and help us improve our services."
        keywords={[
          "surveys",
          "feedback",
          "One80Labs surveys",
          "business surveys",
          "AI assessment",
        ]}
      />
      <div className="px-4 py-5 sm:p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          Available Surveys
        </h2>
        <div className="space-y-4">
          {surveys.map((survey) => (
            <Link
              key={survey.id}
              to={`/survey/${survey.id}`}
              className="block p-4 border rounded-lg hover:border-[#F98B3D] transition-colors"
            >
              <h3 className="text-lg font-medium text-gray-900">
                {survey.title}
              </h3>
              <p className="mt-1 text-sm text-gray-500">{survey.description}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
